import type { DomainLookupResult } from "@agentech/auth-client";
import { createSubdomainRouter, useAuth } from "@agentech/auth-client";
import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { AuthStatusScreen } from "../components/AuthStatusScreen";
import { config } from "../config";

type LoginStep = "email" | "methods" | "password" | "new-password";

export function LoginPage() {
  const { login, loginWithPassword, isAuthenticated } = useAuth();
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [challengeSession, setChallengeSession] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [pendingLogin, setPendingLogin] = useState<{
    identityProvider: "Google" | "Microsoft";
    email: string;
    returnTo?: string;
  } | null>(null);
  const [authMethods, setAuthMethods] = useState<DomainLookupResult["authMethods"]>();
  const resetSuccess = new URLSearchParams(window.location.search).get("reset") === "success";

  const router = createSubdomainRouter(config);

  const passwordRequirements = useMemo(
    () => [
      { label: "At least 12 characters", met: newPassword.length >= 12 },
      { label: "One uppercase letter (A–Z)", met: /[A-Z]/.test(newPassword) },
      { label: "One lowercase letter (a–z)", met: /[a-z]/.test(newPassword) },
      { label: "One number (0–9)", met: /[0-9]/.test(newPassword) },
      { label: "One special character (!@#$%^&*)", met: /[!@#$%^&*]/.test(newPassword) },
    ],
    [newPassword],
  );

  const allRequirementsMet = passwordRequirements.every((r) => r.met);

  // Capture returnTo from URL on mount for deep linking support
  useEffect(() => {
    const returnTo = new URLSearchParams(window.location.search).get("returnTo");
    if (returnTo) {
      sessionStorage.setItem("auth_return_to", returnTo);
    }
  }, []);

  // Perform the OAuth redirect only after the branded loading screen has
  // painted. login() navigates synchronously via window.location, so calling
  // it straight from a click handler leaves the login form on screen until the
  // browser actually leaves. Deferring to an effect lets the loader render
  // first, so the user sees continuous loading instead of the login page.
  useEffect(() => {
    if (pendingLogin) {
      login(pendingLogin);
    }
  }, [pendingLogin, login]);

  const handleEmailSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        const result = await router.handleEmailSubmit(email);

        if (result.shouldRedirect && result.redirectUrl) {
          // Store the return URL before redirecting to subdomain
          const returnTo = new URLSearchParams(window.location.search).get("returnTo");
          if (returnTo) {
            sessionStorage.setItem("auth_return_to", returnTo);
          }
          window.location.href = result.redirectUrl;
          return;
        }

        setAuthMethods(result.authMethods);

        // If only one auth method available, go directly
        if (result.authMethods?.length === 1) {
          const method = result.authMethods[0];
          const returnTo = sessionStorage.getItem("auth_return_to") ?? undefined;
          if (method === "password") {
            setStep("password");
            return;
          } else if (method === "google") {
            setRedirecting(true);
            setPendingLogin({ identityProvider: "Google", email, returnTo });
            return;
          } else if (method === "microsoft") {
            setRedirecting(true);
            setPendingLogin({ identityProvider: "Microsoft", email, returnTo });
            return;
          }
        }

        setStep("methods");
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    },
    [email, router],
  );

  const handleEmailLogin = useCallback(() => {
    setError(null);
    setStep("password");
  }, []);

  const handlePasswordSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        const result = await loginWithPassword({ email, password });

        if (result.success) {
          // Login successful - redirect will happen automatically via isAuthenticated check
          const returnTo =
            sessionStorage.getItem("auth_return_to") || `https://platform.${config.baseDomain}`;
          sessionStorage.removeItem("auth_return_to");
          // Keep the loading UI up until the browser finishes navigating so
          // the login form never flashes between success and redirect.
          setRedirecting(true);
          window.location.href = returnTo;
          return;
        }

        if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
          setChallengeSession(result.challengeSession ?? null);
          setStep("new-password");
          return;
        }

        if (result.challengeName) {
          setError(`Additional verification required: ${result.challengeName}`);
        } else {
          setError(result.error ?? "Login failed");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, loginWithPassword],
  );

  const handleNewPasswordSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        const res = await fetch(`${config.apiBaseUrl}/v1/auth/new-password`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, newPassword, challengeSession }),
        });

        const data = await res.json();

        if (!res.ok) {
          if (res.status === 401) {
            // Session expired — reset to login so user can get a fresh challenge
            setStep("password");
            setNewPassword("");
            setConfirmPassword("");
            setChallengeSession(null);
            setError("Your session expired. Please sign in again to set your password.");
          } else {
            setError(data.error ?? "Failed to set new password");
          }
          setIsLoading(false);
          return;
        }

        // Keep isLoading true — button stays in loading state until page navigates
        const returnTo =
          sessionStorage.getItem("auth_return_to") || `https://platform.${config.baseDomain}`;
        sessionStorage.removeItem("auth_return_to");
        window.location.href = returnTo;
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
        setIsLoading(false);
      }
    },
    [email, newPassword, challengeSession],
  );

  const handleGoogleLogin = useCallback(() => {
    setRedirecting(true);
    const returnTo = sessionStorage.getItem("auth_return_to") ?? undefined;
    setPendingLogin({ identityProvider: "Google", email, returnTo });
  }, [email]);

  const handleMicrosoftLogin = useCallback(() => {
    setRedirecting(true);
    const returnTo = sessionStorage.getItem("auth_return_to") ?? undefined;
    setPendingLogin({ identityProvider: "Microsoft", email, returnTo });
  }, [email]);

  const handleBack = useCallback(() => {
    if (step === "new-password") {
      setStep("password");
      setNewPassword("");
      setConfirmPassword("");
      setChallengeSession(null);
    } else if (step === "password") {
      setStep("methods");
      setPassword("");
    } else {
      setStep("email");
    }
    setError(null);
  }, [step]);

  // Branded loading screen shown while handing off to the authenticated app,
  // so the login form never flashes between login success and navigation.
  const redirectingScreen = (
    <AuthStatusScreen>
      <div className="loading-spinner" />
      <h2>Signing you in</h2>
      <p>Taking you to your workspace…</p>
    </AuthStatusScreen>
  );

  // Redirect if already authenticated
  if (isAuthenticated) {
    const returnTo =
      sessionStorage.getItem("auth_return_to") || `https://platform.${config.baseDomain}`;
    sessionStorage.removeItem("auth_return_to");
    window.location.href = returnTo;
    return redirectingScreen;
  }

  if (redirecting) {
    return redirectingScreen;
  }

  const stepCopy = (() => {
    switch (step) {
      case "email":
        return { title: "Sign in", subtitle: "Enter your email to continue." };
      case "methods":
        return { title: "Sign in", subtitle: "Choose how you'd like to continue." };
      case "password":
        return { title: "Sign in", subtitle: "Enter your password to continue." };
      case "new-password":
        return {
          title: "Set a new password",
          subtitle: "Create a secure password to activate your account.",
        };
    }
  })();

  return (
    <>
      {isLoading && step === "new-password" && (
        <div className="fullscreen-overlay">
          <div className="loading-spinner" />
          <p>Setting up your account...</p>
        </div>
      )}
      <div className="auth-shell">
        <aside className="auth-brand">
          <img src="/agentech-white-logo.svg" alt="Agentech" className="auth-brand-logo" />

          <div className="auth-brand-content">
            <span className="auth-brand-eyebrow">
              <span className="auth-brand-eyebrow-dot" />
              Agentech Platform
            </span>
            <h2 className="auth-brand-title">One platform for your team.</h2>
            <p className="auth-brand-subtitle">
              Sign in once to access your Agentech workspace and every connected tool.
            </p>
            <ul className="auth-brand-features">
              <li>Unified sign-on across your entire workspace</li>
              <li>Enterprise SSO with Google, Microsoft, and SAML</li>
              <li>Every product, one familiar experience</li>
            </ul>
          </div>

          <div className="auth-brand-footer">© {new Date().getFullYear()} Agentech</div>
        </aside>

        <main className="auth-main">
          <div className="auth-main-topbar">
            <img src="/agentech-white-logo.svg" alt="Agentech" className="auth-brand-logo" />
          </div>

          <div className="auth-main-body">
            <div className="auth-card">
              <div className="login-header">
                <h1>{stepCopy.title}</h1>
                <p>{stepCopy.subtitle}</p>
              </div>

              {resetSuccess && step === "email" && (
                <div className="success-message">
                  Password reset successfully. Please sign in with your new password.
                </div>
              )}

              {step === "email" && (
                <form onSubmit={handleEmailSubmit} className="login-form">
                  <div className="form-group">
                    <label htmlFor="email">Email address</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      disabled={isLoading}
                      autoComplete="email"
                      // biome-ignore lint/a11y/noAutofocus: login form primary input
                      autoFocus
                    />
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? <span className="btn-spinner" /> : "Continue"}
                  </button>
                </form>
              )}

              {step === "methods" && (
                <div className="login-methods">
                  <div className="email-display">
                    <button type="button" onClick={handleBack} className="btn-back">
                      &larr;
                    </button>
                    <span>{email}</span>
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <div className="methods-list">
                    {authMethods?.includes("password") && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleEmailLogin}
                        disabled={isLoading}
                      >
                        Continue with Email
                      </button>
                    )}

                    {authMethods?.includes("google") && (
                      <button
                        type="button"
                        className="btn btn-social btn-google"
                        onClick={handleGoogleLogin}
                        disabled={isLoading}
                      >
                        <GoogleIcon />
                        Continue with Google
                      </button>
                    )}

                    {authMethods?.includes("microsoft") && (
                      <button
                        type="button"
                        className="btn btn-social btn-microsoft"
                        onClick={handleMicrosoftLogin}
                        disabled={isLoading}
                      >
                        <MicrosoftIcon />
                        Continue with Microsoft
                      </button>
                    )}

                    {(authMethods?.includes("saml") || authMethods?.includes("oidc")) && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleEmailLogin}
                        disabled={isLoading}
                      >
                        Continue with SSO
                      </button>
                    )}
                  </div>
                </div>
              )}

              {step === "password" && (
                <form onSubmit={handlePasswordSubmit} className="login-form">
                  <div className="email-display">
                    <button type="button" onClick={handleBack} className="btn-back">
                      &larr;
                    </button>
                    <span>{email}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="password">Password</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      disabled={isLoading}
                      autoComplete="current-password"
                      // biome-ignore lint/a11y/noAutofocus: login form primary input
                      autoFocus
                    />
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading ? <span className="btn-spinner" /> : "Sign In"}
                  </button>

                  <div className="forgot-password">
                    <a href="/forgot-password">Forgot password?</a>
                  </div>
                </form>
              )}

              {step === "new-password" && (
                <form onSubmit={handleNewPasswordSubmit} className="login-form">
                  <div className="email-display">
                    <button type="button" onClick={handleBack} className="btn-back">
                      &larr;
                    </button>
                    <span>{email}</span>
                  </div>

                  <div className="form-group">
                    <label htmlFor="new-password">New Password</label>
                    <input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter your new password"
                      required
                      disabled={isLoading}
                      autoComplete="new-password"
                      // biome-ignore lint/a11y/noAutofocus: primary input for this step
                      autoFocus
                    />
                  </div>

                  <ul className="password-req-list">
                    {passwordRequirements.map((req) => (
                      <li key={req.label} className={`password-req-item${req.met ? " met" : ""}`}>
                        <span className="password-req-icon">{req.met ? "✓" : "○"}</span>
                        {req.label}
                      </li>
                    ))}
                  </ul>

                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm Password</label>
                    <input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      required
                      disabled={isLoading}
                      autoComplete="new-password"
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="field-error">Passwords do not match</p>
                    )}
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      isLoading ||
                      !allRequirementsMet ||
                      !confirmPassword ||
                      newPassword !== confirmPassword
                    }
                  >
                    {isLoading ? <span className="btn-spinner" /> : "Confirm & Sign In"}
                  </button>
                </form>
              )}

              <div className="login-footer">
                <p>By continuing, you agree to our Terms of Service and Privacy Policy.</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#00A4EF" d="M1 13h10v10H1z" />
      <path fill="#7FBA00" d="M13 1h10v10H13z" />
      <path fill="#FFB900" d="M13 13h10v10H13z" />
    </svg>
  );
}
